import {
    Checkbox,
    Container,
    createTheme,
    defaultCssVariablesResolver,
    NavLink,
    Radio,
    Switch,
    type CSSVariablesResolver,
    type MantineThemeOverride,
} from '@mantine/core';
import navClasses from './nav.module.css';

const poppinsStack = 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif';

/**
 * Grayscale experiment: the palette is deliberately desaturated while we look
 * at the app without brand colour. Hue is reserved for meaning only —
 * `red` for destructive actions and `orange` for the switched-org chrome.
 *
 * Every decorative palette below aliases this one ramp, so existing
 * `color="violet"` / `color="teal"` call sites desaturate without being edited.
 * Restoring the brand means restoring these arrays, not touching components.
 */
const neutralScale = [
    '#f8f9fa',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#868e96',
    '#495057',
    '#343a40',
    '#212529',
] as const;

export const watchlyAccent = [...neutralScale] as const;

/** The brand gradient, currently a neutral sweep. Single source for components. */
export const brandGradient = { from: '#868e96', to: '#343a40', deg: 135 } as const;

/** Mantine `dark` scale aligned with watchly globals surfaces */
export const watchlyDarkScale = [
    '#C1C2C5',
    '#A6A7AB',
    '#909296',
    '#5c5f66',
    '#373A40',
    '#2C2E33',
    '#25262b',
    '#1a1a22',
    '#14141a',
    '#0d0d12',
] as const;

export const appMantineTheme: MantineThemeOverride = createTheme({
    colors: {
        neutral: [...neutralScale],
        /*
         * Decorative/brand hues are aliased to the neutral ramp for the
         * grayscale pass. Hues that carry meaning keep their real values and are
         * deliberately absent from this list:
         *   red    — destructive actions
         *   orange — acting inside a customer org
         *   green / yellow / teal — data classification (Sport, Commercial,
         *            healthy) on the dashboard KPIs, charts and device badges
         */
        violet: [...neutralScale],
        indigo: [...neutralScale],
        blue: [...neutralScale],
        cyan: [...neutralScale],
        grape: [...neutralScale],
        pink: [...neutralScale],
        watchlyAccent: [...watchlyAccent],
        dark: [...watchlyDarkScale],
    },
    primaryColor: 'neutral',
    // Flipped from the brand shades: a mid-gray primary has too little contrast
    // on light backgrounds, so light mode takes a darker step and dark a lighter.
    primaryShade: { light: 7, dark: 5 },
    defaultRadius: 'md',
    components: {
        NavLink: NavLink.extend({
            classNames: navClasses,
        }),
        /*
         * Mantine leaves the default cursor on these, so they don't read as
         * clickable. Applied to the whole control — track, thumb and label —
         * since all three are valid click targets.
         */
        Switch: Switch.extend({
            styles: {
                root: { cursor: 'pointer' },
                track: { cursor: 'pointer' },
                thumb: { cursor: 'pointer' },
                label: { cursor: 'pointer' },
            },
        }),
        Checkbox: Checkbox.extend({
            styles: {
                input: { cursor: 'pointer' },
                label: { cursor: 'pointer' },
            },
        }),
        Radio: Radio.extend({
            styles: {
                radio: { cursor: 'pointer' },
                label: { cursor: 'pointer' },
            },
        }),
        Container: Container.extend({
            /**
             * Container is the app's page wrapper — every route in AppRoutes is
             * wrapped in one, and it is used nowhere else. Owning the vertical
             * page padding here keeps the header offset identical on every page;
             * previously each page invented its own (48px on the dashboard, 32
             * on Marketplace, 16 elsewhere) and they visibly disagreed.
             *
             * Pages should set horizontal padding only.
             */
            defaultProps: { size: 'lg', pt: 48, pb: 64 },
        }),
    },
});

export const appCssVariablesResolver: CSSVariablesResolver = (theme) => {
    const base = defaultCssVariablesResolver(theme);
    return {
        ...base,
        dark: {
            ...base.dark,
            '--mantine-font-family': poppinsStack,
            '--mantine-font-family-headings': poppinsStack,
            '--mantine-color-body': '#000000',
            '--mantine-color-anchor': 'var(--mantine-color-neutral-4)',
        },
    };
};
